import { useSearchParams } from 'react-router'
import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
} from '#app/components/ui/pagination.tsx'

function generatePageNumbers(currentPage: number, totalPages: number) {
	const pages: (number | 'ellipsis')[] = []

	if (totalPages <= 7) {
		// Show all pages if 7 or fewer
		for (let i = 1; i <= totalPages; i++) {
			pages.push(i)
		}
	} else {
		// Always show first page
		pages.push(1)

		if (currentPage <= 3) {
			// Near the start
			pages.push(2, 'ellipsis', totalPages)
		} else if (currentPage >= totalPages - 2) {
			// Near the end
			pages.push(
				'ellipsis',
				totalPages - 3,
				totalPages - 2,
				totalPages - 1,
				totalPages,
			)
		} else {
			// In the middle
			pages.push(
				'ellipsis',
				currentPage - 1,
				currentPage,
				currentPage + 1,
				'ellipsis',
				totalPages,
			)
		}
	}

	return pages
}

interface BusinessPaginationProps {
	currentPage: number
	totalPages: number
}

export function BusinessPagination({
	currentPage,
	totalPages,
}: BusinessPaginationProps) {
	const [searchParams] = useSearchParams()

	if (totalPages <= 1) return null

	const hasPrev = currentPage > 1
	const hasNext = currentPage < totalPages

	function hrefFor(page: number) {
		const sp = new URLSearchParams(searchParams)
		sp.set('page', String(page))
		return `?${sp.toString()}`
	}

	return (
		<div className="mt-8">
			<Pagination>
				<PaginationContent>
					<PaginationItem>
						<PaginationPrevious
							href={hasPrev ? hrefFor(currentPage - 1) : undefined}
						/>
					</PaginationItem>
					{generatePageNumbers(currentPage, totalPages).map((pageNum, idx) =>
						pageNum === 'ellipsis' ? (
							<PaginationItem key={`ellipsis-${idx}`}>
								<PaginationEllipsis />
							</PaginationItem>
						) : (
							<PaginationItem key={pageNum}>
								<PaginationLink
									href={hrefFor(pageNum)}
									isActive={pageNum === currentPage}
								>
									{pageNum}
								</PaginationLink>
							</PaginationItem>
						),
					)}
					<PaginationItem>
						<PaginationNext
							href={hasNext ? hrefFor(currentPage + 1) : undefined}
						/>
					</PaginationItem>
				</PaginationContent>
			</Pagination>
		</div>
	)
}
